import { Typography, Box, Theme } from '@mui/material';
import {
  DataGridPremium,
  GridColDef,
  GridListViewColDef,
  GridValidRowModel,
  GridRenderCellParams,
  GridInitialState,
  GridTreeDataGroupingCell,
  gridFilteredDescendantCountLookupSelector,
  useGridApiContext,
  useGridApiRef,
  useGridSelector,
  type GridGroupNode,
} from '@mui/x-data-grid-premium';
import { defaultToolbarExportSlotProps } from '@/components/table/toolbarSlotProps';
import { createExcelExportWorker } from '@/utils/excelExportWorker';
import {
  aggregateHistoricalComparisons,
  formatHistoricalCurrency,
  formatHistoricalPercentFixed,
  lookupLiveEndingBalance,
  withHistoricalChangeForRange,
  withLiveEndingBalance,
} from '@/utils/historicalBalance';
import { useGetAccountHistoricalComparisonQuery } from '@/store/accountsApi';
import {
  historicalComparisonQueryArg,
  isCustomGroupBy,
} from './historicalComparisonQueryArg';
import { skipToken } from '@reduxjs/toolkit/query';
import { useMemo, type ComponentProps } from 'react';
import { APP_DATE_DISPLAY_FORMAT } from '@/constants/dateFormats';
import { formatAppDate } from '@/utils/date';
import { filterByInScopeIds } from '@/utils/historicalAccountScope';
import { DataRangeFilterEnum } from '@/constants/dates';
import { useLocalization } from '@/hooks/useLocalization';
import { useAccountCreatedDates } from '@/hooks/useAccountCreatedDates';
import { useInScopeAccountIds } from '@/hooks/useInScopeAccountIds';
import { mobileWidth } from '@/constants';
import { GRID_INITIAL_PAGINATION } from '@/constants/pagination';
import { useMediaQuery } from '@mui/material';
import { AccountHistoricalComparisonItem } from '@/models/AccountViewModel';
import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import CustomToolbarForNative from '@/components/CustomToolbarForNative';
import XsPreferencesDrawerToolbar from '@/components/table/XsPreferencesDrawerToolbar';
import { Capacitor } from '@capacitor/core';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { GRID_COLUMN_WIDTH_NARROW } from '@/constants/dimensions';
import { GridContainer } from '@/components/grids/GridContainer';
import {
  groupHistoricalRecords,
  isSplitGroup,
  type GroupTotals,
  type GroupTotalsByPath,
} from './balanceSheetHistoricalGroups';
import {
  SECTION_SUBTOTAL,
  createGroupTotalsLookup,
  createSectionSubtotalAggregation,
  sectionSubtotal,
  type GroupTotalsLookup,
} from './balanceSheetHistoricalAggregation';

interface GridRow extends GridValidRowModel {
  id: string;
  Name: string;
  groupName: string;
  groupSort: string | number;
  isGroupTotal?: boolean;
  sortIndex: number;
  isAsset: boolean;
  classificationSort: number;
  OldDollarBalance: number | null;
  DollarBalance: number | null;
  DollarChange: number | null;
  PercentChange: number | null;
  AccountIcon?: string;
}

interface TreeGridRow extends GridRow {
  parentId?: string | null;
  path?: string[];
  isGrandTotal?: boolean;
}

interface GrandTotal {
  OldDollarBalance: number | null;
  DollarBalance: number | null;
  DollarChange: number | null;
  PercentChange: number | null;
}

type BalanceField = 'OldDollarBalance' | 'DollarBalance' | 'DollarChange';

export interface BalanceSheetHistoricalFilters {
  excludedAccounts?: number[];
  excludedCategories?: number[];
  excludedAccountTypes?: number[];
  excludedInstitutions?: number[];
}

interface BalanceSheetHistoricalGridProps {
  dateFilter: DataRangeFilterEnum;
  startDate?: string;
  endDate?: string;
  accountId?: number;
  /** Accounts-page balance for `accountId` when the accounts list omits it. */
  liveBalance?: number;
  groupBy?: string;
  excludedAccounts?: number[];
  excludedCategories?: number[];
  excludedAccountTypes?: number[];
  excludedInstitutions?: number[];
  isWidget?: boolean;
}

const INITIAL_GRID_STATE_GROUPED: GridInitialState = {
  sorting: {
    sortModel: [
      { field: 'groupName', sort: 'asc' },
      { field: 'sortIndex', sort: 'asc' },
    ],
  },
  aggregation: {
    model: {
      OldDollarBalance: SECTION_SUBTOTAL,
      DollarBalance: SECTION_SUBTOTAL,
      DollarChange: SECTION_SUBTOTAL,
      PercentChange: SECTION_SUBTOTAL,
    },
  },
  pagination: GRID_INITIAL_PAGINATION,
};

const gridStyles = {
  fontSize: '14px',
  border: 'none',
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: (theme: Theme) =>
      theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.08)'
        : theme.palette.grey[100],
    borderBottom: '1px solid rgba(224, 224, 224, 0.2)',
  },
  '& .MuiDataGrid-cell, & .MuiDataGrid-columnHeader': {
    fontSize: '14px',
  },
  '& .MuiDataGrid-cell': {
    borderBottom: '1px solid rgba(224, 224, 224, 0.2)',
    padding: '0px 10px 0px 10px',
    '&.MuiDataGrid-cell--tree': {
      paddingLeft: 0,
    },
  },
  '& .MuiDataGrid-row:last-child .MuiDataGrid-cell': {
    borderBottom: 'none',
  },
  '& .MuiDataGrid-row': {
    '&.MuiDataGrid-row--tree': {
      bgcolor: (theme: Theme) =>
        theme.palette.mode === 'dark'
          ? theme.palette.grey[900]
          : theme.palette.grey[100],
    },
    '& .MuiDataGrid-treeDataGroupingCellToggle': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  },
};

const groupKeyLabel = (key: GridGroupNode['groupingKey']): string => {
  if (key == null) return '';
  return typeof key === 'string' ? key : String(key);
};

const useFilteredAccountCount = (
  rowNode: GridGroupNode,
  isSplit: boolean,
): number => {
  const apiRef = useGridApiContext();
  const filteredCounts = useGridSelector(
    apiRef,
    gridFilteredDescendantCountLookupSelector,
  );
  if (!isSplit) return filteredCounts[rowNode.id] ?? 0;
  return rowNode.children.reduce<number>(
    (sum, sectionId) => sum + (filteredCounts[sectionId] ?? 0),
    0,
  );
};

const toDisplayString = (
  value: string | number | boolean | null | undefined,
): string => {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return '';
};

const getAccountIcon = (data: GridRow) => {
  if (data.AccountIcon) {
    return <span className={`fa ${data.AccountIcon}`} />;
  }
  return null;
};

const createBaseColumnProps = () => ({
  headerAlign: 'right' as const,
  align: 'right' as const,
});

// Note: hooks (like useMediaQuery) must be called inside a React component.

const createNameColumn = (
  t: (key: string) => string,
): GridColDef<TreeGridRow> => ({
  field: 'Name',
  headerName: t('name'),
  flex: 1,
  minWidth: 300,
  renderCell: (params: GridRenderCellParams<TreeGridRow>) => {
    try {
      const { row, rowNode } = params;
      const isTotal = row.isGroupTotal;
      const isGrandTotal = row.isGrandTotal;

      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            height: '100%',
            pl: rowNode.depth > 0 ? 2 : 0,
          }}
        >
          <Typography
            variant='body2'
            sx={{
              fontWeight: isTotal || isGrandTotal ? 600 : 400,
              color: 'text.primary',
              flexGrow: 1,
            }}
          >
            {row.Name}
          </Typography>
          {!isTotal && getAccountIcon(row)}
        </Box>
      );
    } catch (error) {
      console.error('Error in Name renderCell:', error);
      return null;
    }
  },
});

const SplitGroupGroupingCell = (
  props: ComponentProps<typeof GridTreeDataGroupingCell>,
) => {
  const accountCount = useFilteredAccountCount(props.rowNode, true);

  return (
    <GridTreeDataGroupingCell
      {...props}
      hideDescendantCount
      formattedValue={`${groupKeyLabel(props.rowNode.groupingKey)} (${String(accountCount)})`}
    />
  );
};

const ListViewGroupHeader = ({
  rowNode,
  label,
  totals,
  emptyLabel,
}: {
  rowNode: GridGroupNode;
  label: string;
  totals: GroupTotals | undefined;
  emptyLabel: string;
}) => {
  const apiRef = useGridApiContext();
  const isSplit = isSplitGroup(totals);
  const count = useFilteredAccountCount(rowNode, isSplit);
  const isExpanded = !!rowNode.childrenExpanded;

  const handleToggleExpand = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    apiRef.current.setRowChildrenExpansion(rowNode.id, !isExpanded);
  };

  return (
    <Box sx={{ width: '100%', py: 1.5, pl: rowNode.depth * 2 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          width: '100%',
          px: 0,
        }}
      >
        <Box
          onClick={handleToggleExpand}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 28,
            height: 28,
            flex: '0 0 0px',
            cursor: 'pointer',
          }}
        >
          {isExpanded ? (
            <ExpandMoreIcon fontSize='small' />
          ) : (
            <ChevronRightIcon fontSize='small' />
          )}
        </Box>

        <Typography
          variant='body2'
          color='text.primary'
          sx={{ fontWeight: '600 !important', flex: 1, minWidth: 0 }}
        >
          {count > 0 ? `${label} (${String(count)})` : label}
        </Typography>

        <Typography
          variant='body2'
          color='text.primary'
          sx={{
            fontWeight: '600 !important',
            textAlign: 'right',
            whiteSpace: 'nowrap',
          }}
        >
          {totals && !isSplit
            ? formatHistoricalCurrency(totals.DollarBalance, emptyLabel)
            : ''}
        </Typography>
      </Box>
    </Box>
  );
};

const getValueColor = (value: number | null | undefined) => {
  if (value && value > 0) return 'success.main';
  if (value && value < 0) return 'error.main';
  return 'text.secondary';
};

const createBalanceColumn = (
  field: BalanceField,
  headerName: string,
  getGroupTotals: GroupTotalsLookup,
  emptyLabel: string,
): GridColDef<TreeGridRow> => ({
  ...createBaseColumnProps(),
  field,
  headerName,
  width: GRID_COLUMN_WIDTH_NARROW,
  renderCell: (params: GridRenderCellParams<TreeGridRow, number>) => {
    if (params.rowNode.type === 'group') {
      const value = sectionSubtotal(getGroupTotals(params.rowNode), field);

      if (value !== undefined) {
        return (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
          >
            <Typography
              variant='body2'
              sx={{
                color: getValueColor(value),
                fontWeight: 'bold',
              }}
            >
              {formatHistoricalCurrency(value, emptyLabel)}
            </Typography>
          </Box>
        );
      }
      return null;
    }
    const value = params.value;
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
        }}
      >
        <Typography
          variant='body2'
          sx={{ color: getValueColor(value) }}
        >
          {formatHistoricalCurrency(value, emptyLabel)}
        </Typography>
      </Box>
    );
  },
});

const createPercentageColumn = (
  t: (key: string, defaultVal: string) => string,
  getGroupTotals: GroupTotalsLookup,
  emptyLabel: string,
): GridColDef<TreeGridRow> => ({
  ...createBaseColumnProps(),
  field: 'PercentChange',
  headerName: t('grid.percentChange', '% Change'),
  width: GRID_COLUMN_WIDTH_NARROW,
  renderCell: (params: GridRenderCellParams<TreeGridRow, number>) => {
    if (params.rowNode.type === 'group') {
      const value = sectionSubtotal(
        getGroupTotals(params.rowNode),
        'PercentChange',
      );

      if (value !== undefined) {
        return (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
          >
            <Typography
              variant='body2'
              sx={{
                color: getValueColor(value),
                fontWeight: 'bold',
              }}
            >
              {formatHistoricalPercentFixed(value, emptyLabel)}
            </Typography>
          </Box>
        );
      }
      return null;
    }

    const value = params.value;

    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
        }}
      >
        <Typography
          variant='body2'
          sx={{ color: getValueColor(value) }}
        >
          {formatHistoricalPercentFixed(value, emptyLabel)}
        </Typography>
      </Box>
    );
  },
});

interface ListViewTotalLine {
  label: string;
  value: string;
  color: string;
}

const ListViewFooter = ({ lines }: { lines: ListViewTotalLine[] }) => (
  <Box
    sx={{
      borderTop: (theme: Theme) => `2px solid ${theme.palette.divider}`,
      px: 3,
      py: 2,
    }}
  >
    <Typography
      variant='body2'
      sx={{ fontWeight: 600 }}
    >
      Total:
    </Typography>
    {lines.map(({ label, value, color }) => (
      <Box
        key={label}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 1,
          mt: 0.5,
        }}
      >
        <Typography
          variant='body2'
          color='text.secondary'
        >
          {label}
        </Typography>
        <Typography
          variant='body2'
          sx={{ fontWeight: 600, whiteSpace: 'nowrap', color }}
        >
          {value}
        </Typography>
      </Box>
    ))}
  </Box>
);

const CustomFooter = ({
  grandTotal,
  emptyLabel,
}: {
  grandTotal: GrandTotal | null;
  emptyLabel: string;
}) => (
  <Box
    sx={{
      borderTop: (theme: Theme) => `2px solid ${theme.palette.divider}`,
      display: 'grid',
      gridTemplateColumns: 'minmax(300px, 1fr) repeat(4, 150px)',
      alignItems: 'center',
      px: 2,
      py: 2,
    }}
  >
    <Typography
      variant='body2'
      sx={{ fontWeight: 600, pl: 1 }}
    >
      Total:
    </Typography>
    <Typography
      variant='body2'
      sx={{
        fontWeight: 600,
        textAlign: 'right',
        pr: 2,
        color: getValueColor(grandTotal?.OldDollarBalance),
      }}
    >
      {formatHistoricalCurrency(grandTotal?.OldDollarBalance, emptyLabel)}
    </Typography>
    <Typography
      variant='body2'
      sx={{
        fontWeight: 600,
        textAlign: 'right',
        pr: 2,
        color: getValueColor(grandTotal?.DollarBalance),
      }}
    >
      {formatHistoricalCurrency(grandTotal?.DollarBalance, emptyLabel)}
    </Typography>
    <Typography
      variant='body2'
      sx={{
        fontWeight: 600,
        textAlign: 'right',
        pr: 2,
        color: getValueColor(grandTotal?.DollarChange),
      }}
    >
      {formatHistoricalCurrency(grandTotal?.DollarChange, emptyLabel)}
    </Typography>
    <Typography
      variant='body2'
      sx={{
        fontWeight: 600,
        textAlign: 'right',
        pr: 2,
        color: getValueColor(grandTotal?.PercentChange),
      }}
    >
      {formatHistoricalPercentFixed(grandTotal?.PercentChange, emptyLabel)}
    </Typography>
  </Box>
);

/** Filter historical records by exclusion lists (same logic as balance sheet current view). */
function filterHistoricalRecords(
  records: AccountHistoricalComparisonItem[],
  filters: BalanceSheetHistoricalFilters,
): AccountHistoricalComparisonItem[] {
  const {
    excludedAccounts = [],
    excludedCategories = [],
    excludedAccountTypes = [],
    excludedInstitutions = [],
  } = filters;
  const hasFilters =
    excludedAccounts.length > 0 ||
    excludedCategories.length > 0 ||
    excludedAccountTypes.length > 0 ||
    excludedInstitutions.length > 0;
  if (!hasFilters) return records;

  return records.filter((record) => {
    if (excludedAccounts.length > 0 && excludedAccounts.includes(record.Id)) {
      return false;
    }
    if (
      excludedCategories.length > 0 &&
      excludedCategories.includes(record.GroupId)
    ) {
      return false;
    }
    const recordWithOptional = record as AccountHistoricalComparisonItem & {
      InstitutionId?: number;
      AccountType?: number;
    };
    if (
      excludedInstitutions.length > 0 &&
      recordWithOptional.InstitutionId != null &&
      excludedInstitutions.includes(recordWithOptional.InstitutionId)
    ) {
      return false;
    }
    if (
      excludedAccountTypes.length > 0 &&
      recordWithOptional.AccountType != null &&
      excludedAccountTypes.includes(recordWithOptional.AccountType)
    ) {
      return false;
    }
    return true;
  });
}

const BalanceSheetHistoricalGrid = ({
  startDate,
  endDate,
  accountId,
  liveBalance,
  groupBy = 'none',
  excludedAccounts = [],
  excludedCategories = [],
  excludedAccountTypes = [],
  excludedInstitutions = [],
  isWidget = false,
}: BalanceSheetHistoricalGridProps) => {
  const { t } = useLocalization();
  const emptyLabel = t('common.emdash', '—');
  const authIsLoaded = useAuthIsLoaded();
  const isMobileMatch = useMediaQuery(mobileWidth);
  const isSingleAccountView = !!accountId;
  const isCustomGroup = isCustomGroupBy(groupBy);
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- guard Capacitor.getPlatform being absent on non-Capacitor runtimes (typed non-nullable but can be undefined when the shim isn't initialized)
  const isNative = Capacitor.getPlatform && Capacitor.getPlatform() !== 'web';
  const {
    accountIds: inScopeAccountIds,
    balancesByAccountId,
    isLoading: isScopeLoading,
  } = useInScopeAccountIds({
    endDate,
    skipScope: isSingleAccountView,
    excludedAccounts,
    excludedAccountTypes,
    excludedCategories,
    excludedInstitutions,
  });
  const {
    createdDates,
    isLoading: areCreatedDatesLoading,
    isError: createdDatesFailed,
  } = useAccountCreatedDates(endDate);

  const { data, isLoading, isFetching, error } =
    useGetAccountHistoricalComparisonQuery(
      authIsLoaded && startDate && endDate
        ? historicalComparisonQueryArg({
            startDate,
            endDate,
            groupBy,
            accountId,
            excludedAccounts,
            excludedAccountTypes,
            excludedCategories,
            excludedInstitutions,
          })
        : skipToken,
      {
        skip: !authIsLoaded || !startDate || !endDate,
        refetchOnMountOrArgChange: true,
      },
    );

  const historicalFilters: BalanceSheetHistoricalFilters = useMemo(
    () => ({
      excludedAccounts,
      excludedCategories,
      excludedAccountTypes,
      excludedInstitutions,
    }),
    [
      excludedAccounts,
      excludedCategories,
      excludedAccountTypes,
      excludedInstitutions,
    ],
  );

  const sectionLabels = useMemo(
    () => ({
      assets: t('assets', 'Assets'),
      liabilities: t('liabilities', 'Liabilities'),
    }),
    [t],
  );

  const { rows, groupTotalsByPath } = useMemo(() => {
    const emptyTotals: GroupTotalsByPath = new Map();
    const sourceRecords =
      areCreatedDatesLoading || createdDatesFailed ? [] : (data?.Records ?? []);
    const records = isSingleAccountView
      ? sourceRecords
      : filterByInScopeIds(
          sourceRecords,
          (record) => record.Id,
          inScopeAccountIds,
        );

    const openRecords = records.filter((record) => !record.ClosedDate);
    const activeRecords = filterHistoricalRecords(
      openRecords,
      historicalFilters,
    ).map((record) =>
      withHistoricalChangeForRange(
        withLiveEndingBalance(
          record,
          lookupLiveEndingBalance(
            record.Id,
            balancesByAccountId,
            accountId,
            liveBalance,
          ),
          endDate,
        ),
        {
          createdDate: createdDates.get(record.Id),
          startDate,
          endDate,
        },
      ),
    );

    if (isSingleAccountView) {
      return {
        rows: activeRecords.map((record) => ({
          ...record,
          id: record.Id.toString(),
          groupName: '',
          groupSort: 0,
          isAsset: record.IsAsset,
          classificationSort: 0,
          sortIndex: 0,
        })),
        groupTotalsByPath: emptyTotals,
      };
    }

    const getGroupName = (record: AccountHistoricalComparisonItem): string => {
      if (isCustomGroup) {
        return record.GroupName || 'Not Set';
      }
      switch (groupBy) {
        case 'institution':
          return record.InstitutionName || 'No Institution';
        case 'data source':
          // Data for this grouping is not available in this component's API response.
          // Falling back to classification grouping.
          return record.GroupName || 'Unclassified';
        case 'none':
        default:
          return record.GroupName || 'Unclassified';
      }
    };

    const { entries, totalsByPath } = groupHistoricalRecords(
      activeRecords,
      getGroupName,
      sectionLabels,
    );

    return {
      rows: entries.map(
        ({ record, groupName, path, sortIndex }): TreeGridRow => ({
          ...record,
          id: record.Id.toString(),
          groupName,
          groupSort: sortIndex,
          isAsset: record.IsAsset,
          classificationSort: sortIndex,
          sortIndex,
          isGroupTotal: false,
          OldDollarBalance: record.OldDollarBalance,
          DollarBalance: record.DollarBalance,
          DollarChange: record.DollarChange,
          PercentChange: record.PercentChange,
          path,
        }),
      ),
      groupTotalsByPath: totalsByPath,
    };
  }, [
    areCreatedDatesLoading,
    createdDates,
    createdDatesFailed,
    data,
    accountId,
    balancesByAccountId,
    endDate,
    inScopeAccountIds,
    isSingleAccountView,
    liveBalance,
    groupBy,
    historicalFilters,
    isCustomGroup,
    sectionLabels,
    startDate,
  ]);

  const grandTotal = useMemo(() => {
    if (!rows.length || isSingleAccountView) return null;

    return aggregateHistoricalComparisons(rows);
  }, [rows, isSingleAccountView]);

  const apiRef = useGridApiRef();
  const getGroupTotals = useMemo(
    () => createGroupTotalsLookup(groupTotalsByPath, apiRef),
    [groupTotalsByPath, apiRef],
  );
  const aggregationFunctions = useMemo(
    () => ({
      [SECTION_SUBTOTAL]: createSectionSubtotalAggregation(
        getGroupTotals,
        apiRef,
      ),
    }),
    [getGroupTotals, apiRef],
  );

  const listViewTotalLines: ListViewTotalLine[] = useMemo(
    () => [
      {
        label: startDate
          ? formatAppDate(startDate, { format: APP_DATE_DISPLAY_FORMAT })
          : '',
        value: formatHistoricalCurrency(
          grandTotal?.OldDollarBalance,
          emptyLabel,
        ),
        color: getValueColor(grandTotal?.OldDollarBalance),
      },
      {
        label: endDate
          ? formatAppDate(endDate, { format: APP_DATE_DISPLAY_FORMAT })
          : '',
        value: formatHistoricalCurrency(grandTotal?.DollarBalance, emptyLabel),
        color: getValueColor(grandTotal?.DollarBalance),
      },
      {
        label: t('grid.dollarChange', '$ Change'),
        value: formatHistoricalCurrency(grandTotal?.DollarChange, emptyLabel),
        color: getValueColor(grandTotal?.DollarChange),
      },
      {
        label: t('grid.percentChange', '% Change'),
        value: formatHistoricalPercentFixed(
          grandTotal?.PercentChange,
          emptyLabel,
        ),
        color: getValueColor(grandTotal?.PercentChange),
      },
    ],
    [emptyLabel, endDate, grandTotal, startDate, t],
  );

  const columns: GridColDef<TreeGridRow>[] = useMemo(
    () => [
      createNameColumn(t),
      createBalanceColumn(
        'OldDollarBalance',
        startDate
          ? `${formatAppDate(startDate, { format: APP_DATE_DISPLAY_FORMAT })} ${t(
              'grid.balance',
              'Value',
            )}`
          : '',
        getGroupTotals,
        emptyLabel,
      ),
      createBalanceColumn(
        'DollarBalance',
        endDate
          ? `${formatAppDate(endDate, { format: APP_DATE_DISPLAY_FORMAT })} ${t(
              'grid.balance',
              'Value',
            )}`
          : '',
        getGroupTotals,
        emptyLabel,
      ),
      createBalanceColumn(
        'DollarChange',
        t('grid.dollarChange', '$ Change'),
        getGroupTotals,
        emptyLabel,
      ),
      createPercentageColumn(t, getGroupTotals, emptyLabel),
    ],
    [emptyLabel, startDate, endDate, t, getGroupTotals],
  );

  const listViewColDef: GridListViewColDef<TreeGridRow> = useMemo(
    () => ({
      field: 'listColumn',
      renderCell: (
        params: GridRenderCellParams<
          TreeGridRow,
          string | number | boolean | null
        >,
      ) => {
        const { row, rowNode, api } = params;
        const visibilityModel = api.state.columns.columnVisibilityModel;
        const isFieldVisible = (field: string) =>
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-boolean-literal-compare -- visible columns are omitted from the model; only an explicit false hides them
          visibilityModel[field] !== false;

        const startDateLabel = startDate
          ? formatAppDate(startDate, { format: APP_DATE_DISPLAY_FORMAT })
          : '';
        const endDateLabel = endDate
          ? formatAppDate(endDate, { format: APP_DATE_DISPLAY_FORMAT })
          : '';

        if (!isSingleAccountView && rowNode.type === 'group') {
          return (
            <ListViewGroupHeader
              rowNode={rowNode}
              label={
                groupKeyLabel(rowNode.groupingKey) ||
                toDisplayString(params.value)
              }
              totals={getGroupTotals(rowNode)}
              emptyLabel={emptyLabel}
            />
          );
        }

        const Line = ({
          left,
          right,
          rightColor = 'text.secondary',
        }: {
          left: string;
          right: string;
          rightColor?: string;
        }) => (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              gap: 1,
              mt: 0.5,
              justifyContent: 'space-between',
            }}
          >
            <Typography
              variant='body2'
              color='text.secondary'
              sx={{
                fontWeight: 400,
                flex: '1 1 auto',
                minWidth: 0,
                pr: 1,
              }}
            >
              {left}
            </Typography>
            <Typography
              variant='body2'
              sx={{
                fontWeight: 400,
                textAlign: 'right',
                whiteSpace: 'nowrap',
                color: rightColor,
                flex: '0 0 auto',
                ml: 'auto',
              }}
            >
              {right}
            </Typography>
          </Box>
        );

        const showName = isFieldVisible('Name');
        const showStart = isFieldVisible('OldDollarBalance');
        const showEnd = isFieldVisible('DollarBalance');
        const showDollarChange = isFieldVisible('DollarChange');
        const showPercentChange = isFieldVisible('PercentChange');

        const dollarChange = row.DollarChange;
        const percentChange = row.PercentChange;

        return (
          <Box sx={{ width: '100%', py: 2 }}>
            {showName ? (
              <Typography
                variant='body2'
                color='text.primary'
                sx={{ fontWeight: '600 !important' }}
              >
                {row.Name}
              </Typography>
            ) : null}

            {showStart ? (
              <Line
                left={startDateLabel}
                right={formatHistoricalCurrency(
                  row.OldDollarBalance,
                  emptyLabel,
                )}
              />
            ) : null}

            {showEnd ? (
              <Line
                left={endDateLabel}
                right={formatHistoricalCurrency(row.DollarBalance, emptyLabel)}
              />
            ) : null}

            {showDollarChange ? (
              <Line
                left={t('grid.dollarChange', '$ Change')}
                right={formatHistoricalCurrency(dollarChange, emptyLabel)}
                rightColor={getValueColor(dollarChange)}
              />
            ) : null}

            {showPercentChange ? (
              <Line
                left={t('grid.percentChange', '% Change')}
                right={formatHistoricalPercentFixed(percentChange, emptyLabel)}
                rightColor={getValueColor(percentChange)}
              />
            ) : null}
          </Box>
        );
      },
    }),
    [emptyLabel, endDate, getGroupTotals, isSingleAccountView, startDate, t],
  );

  if (error || createdDatesFailed) {
    return (
      <Typography
        color='error'
        sx={{ p: 3 }}
      >
        {t('errorLoadingBalanceSheetData', 'Error loading balance sheet data')}
      </Typography>
    );
  }

  const CustomGroupingCell = (
    params: GridRenderCellParams<TreeGridRow, string | number | boolean | null>,
  ) => {
    if (params.row.isGroupTotal || params.rowNode.type !== 'group') {
      return null;
    }

    const { rowNode } = params;
    if (!isSplitGroup(getGroupTotals(rowNode))) {
      return (
        <GridTreeDataGroupingCell
          {...params}
          rowNode={rowNode}
        />
      );
    }

    return (
      <SplitGroupGroupingCell
        {...params}
        rowNode={rowNode}
      />
    );
  };

  return (
    <Box sx={{ px: 2, mt: 3, width: '100%' }}>
      <GridContainer isWidget={isWidget}>
        <DataGridPremium
          apiRef={apiRef}
          rows={rows}
          columns={columns}
          listView={isMobileMatch}
          {...(isMobileMatch ? { listViewColumn: listViewColDef } : {})}
          {...(isMobileMatch ? { getRowHeight: () => 'auto' as const } : {})}
          treeData={!isSingleAccountView}
          defaultGroupingExpansionDepth={-1}
          getTreeDataPath={
            !isSingleAccountView
              ? (row: TreeGridRow) => row.path || []
              : undefined
          }
          groupingColDef={
            !isSingleAccountView
              ? {
                  hideDescendantCount: false,
                  renderCell: CustomGroupingCell,
                }
              : undefined
          }
          showToolbar
          disablePivoting
          initialState={!isSingleAccountView ? INITIAL_GRID_STATE_GROUPED : {}}
          aggregationFunctions={aggregationFunctions}
          getAggregationPosition={(node) =>
            node.depth === -1 ? null : 'inline'
          }
          slots={{
            columnMenu: () => null,
            footer: () =>
              !isSingleAccountView ? (
                isMobileMatch ? (
                  <ListViewFooter lines={listViewTotalLines} />
                ) : (
                  <CustomFooter
                    grandTotal={grandTotal}
                    emptyLabel={emptyLabel}
                  />
                )
              ) : null,
            ...(isNative
              ? {
                  toolbar: (props) => (
                    <CustomToolbarForNative
                      {...props}
                      excelWorker={createExcelExportWorker}
                    />
                  ),
                }
              : isMobileMatch
                ? { toolbar: XsPreferencesDrawerToolbar }
                : {}),
          }}
          slotProps={{
            loadingOverlay: {
              variant: 'linear-progress',
              noRowsVariant: 'skeleton',
            },
            // Prevent users from hiding internal grouping columns (tree data) in the Columns drawer.
            // This keeps "Groups" always visible when grouping is enabled.
            columnsManagement: {
              getTogglableColumns: (cols: GridColDef[]) =>
                cols
                  .filter((c) => c.hideable !== false)
                  .map((c) => c.field)
                  .filter(
                    (field) =>
                      field !== 'actions' &&
                      field !== 'listColumn' &&
                      field !== '__list__' &&
                      !field.startsWith('__'),
                  ),
            },
            columnsPanel: {
              getTogglableColumns: (cols: GridColDef[]) =>
                cols
                  .filter((c) => c.hideable !== false)
                  .map((c) => ({ field: c.field }))
                  .filter(
                    (c) =>
                      c.field !== 'actions' &&
                      c.field !== 'listColumn' &&
                      c.field !== '__list__' &&
                      !c.field.startsWith('__'),
                  ),
            },
            ...(isMobileMatch
              ? { toolbar: { showQuickFilter: true } }
              : {
                  toolbar: {
                    ...defaultToolbarExportSlotProps,
                    showQuickFilter: true,
                    excelOptions: {
                      ...defaultToolbarExportSlotProps.excelOptions,
                      worker: createExcelExportWorker,
                    },
                  },
                }),
          }}
          disableColumnMenu
          disableRowSelectionOnClick
          loading={
            isLoading || isFetching || isScopeLoading || areCreatedDatesLoading
          }
          sx={(theme) => ({
            ...gridStyles,
            ...(isMobileMatch && {
              width: '100%',
              maxWidth: '100%',
              minWidth: 0,
              overflowX: 'hidden',
              '& .MuiDataGrid-columnHeaders': {
                display: 'none',
              },
              '& .MuiDataGrid-toolbarContainer': {
                width: '100%',
                maxWidth: '100%',
              },
              '& .MuiDataGrid-main': {
                width: '100%',
                maxWidth: '100%',
                overflowX: 'hidden',
              },
              '& .MuiDataGrid-virtualScroller': {
                width: '100%',
                maxWidth: '100%',
                overflowX: 'hidden',
              },
              '& .MuiDataGrid-virtualScrollerRenderZone': {
                width: '100% !important',
                minWidth: '100% !important',
                maxWidth: '100% !important',
              },
              '& .MuiDataGrid-virtualScrollerContent': {
                width: '100% !important',
                minWidth: '100% !important',
                maxWidth: '100% !important',
              },
              '& .MuiDataGrid-virtualScrollerContent > div': {
                width: '100% !important',
                minWidth: '100% !important',
                maxWidth: '100% !important',
              },
              '& .MuiDataGrid-row': {
                display: 'flex',
                flexWrap: 'nowrap',
                width: '100% !important',
                minWidth: '100% !important',
                maxWidth: '100% !important',
              },
              '& .MuiDataGrid-cell': {
                display: 'flex',
                flexWrap: 'nowrap',
                width: '100%',
                minWidth: '100%',
                maxWidth: '100%',
                flex: '1 1 0',
                px: 3,
              },
              '& .MuiDataGrid-cellContent': {
                width: '100%',
                flex: 1,
              },
              [theme.breakpoints.down(800)]: {
                '& .MuiDataGrid-cell': {
                  width: '100% !important',
                  minWidth: '100% !important',
                  display: 'block !important',
                },
              },
            }),
          })}
        />
      </GridContainer>
    </Box>
  );
};

export default BalanceSheetHistoricalGrid;
